import React, { MouseEvent, useRef } from 'react';
import { useSlateStatic } from 'slate-react';
import { Editor, Element } from 'slate';
import { IconButton, Tooltip, icons } from '@ublend-npm/aulaui-next';
import { TooltipTitle } from '../../HotkeyLabel/HotkeyLabel.styled';
import { AulaRange } from '../../../types';
import {
  orderedListType,
  unorderedListType,
} from '../../../plugins/list/listType';
import {
  convertListToText,
  convertTextToList,
  getIsButtonSelectedFromSelection,
  getIsButtonDisabledFromSelection,
} from './utils';

interface ListButtonProps {
  lastActiveSelection: AulaRange;
  label: string;
  buttonType: 'Unordered' | 'Ordered';
}

export const ListButton = ({
  lastActiveSelection,
  label,
  buttonType,
}: ListButtonProps) => {
  const editor = useSlateStatic();
  const ref = useRef<HTMLButtonElement>(null);

  const isOrderedListButton = buttonType === 'Ordered';
  const listType = isOrderedListButton ? orderedListType : unorderedListType;

  const isButtonSelected = getIsButtonSelectedFromSelection(
    editor,
    isOrderedListButton
  );

  const isButtonDisabled = getIsButtonDisabledFromSelection(
    editor,
    isOrderedListButton
  );

  const toggleList = (
    event:
      | MouseEvent<HTMLButtonElement>
      | (React.KeyboardEvent & { detail: 0 | 1 })
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const selectedElements = Array.from(
      Editor.nodes(editor, {
        at: lastActiveSelection,
        mode: 'lowest',
        match: (n) => Element.isElement(n) && !Editor.isInline(editor, n),
      })
    );
    if (isButtonSelected) {
      convertListToText(editor, selectedElements);
    } else {
      convertTextToList(editor, selectedElements, listType);
    }
  };

  return (
    <Tooltip title={<TooltipTitle>{label}</TooltipTitle>}>
      <div>
        <IconButton
          label={label}
          icon={
            isOrderedListButton
              ? icons.FormatListNumbered
              : icons.FormatListBulleted
          }
          type="secondary"
          size="small"
          disabled={isButtonDisabled}
          selected={isButtonSelected}
          onClick={toggleList}
          data-testid={`data-testid-${label}`}
          aria-label={label}
          disableRipple={false}
          buttonAttributes={{
            ref,
          }}
          onMouseDown={(e: React.SyntheticEvent) => e.preventDefault()}
        />
      </div>
    </Tooltip>
  );
};
